import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './CustomerGeneratedJDs.css';
import {
  Search,
  Plus,
  FileText,
  Eye,
  Download,
  Pencil,
  Trash2,
  Calendar,
  Loader2,
  X,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  SlidersHorizontal,
  FileUp,
} from 'lucide-react';

const ITEMS_PER_PAGE = 10;

const CustomerGeneratedJDs = ({ customerId }) => {
  const [jds, setJds] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search, Sort, Pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  /* ── Fetch ───────────────────────────────────────────────── */
  useEffect(() => {
    const fetchJDs = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/job-description`);

        // Only keep JDs where creatorId matches logged-in customer
        const myJds = res.data.filter((jd) => jd.creatorId === customerId);

        setJds(myJds);
      } catch (error) {
        console.error('Error fetching JDs', error);
      } finally {
        setLoading(false);
      }
    };

    if (customerId) fetchJDs();
  }, [customerId]);

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortField, sortDir]);

  /* ── Handlers ────────────────────────────────────────────── */

  // --- Handle Edit Navigation ---
  const handleEdit = (id) => {
    navigate(`/customer-dashboard?tab=generator&id=${id}`);
  };

  // --- Handle Download (Simplified for GCS) ---
  const handleDownload = (pdfPath, jdName) => {
    if (!pdfPath) return alert('No PDF file found for this JD.');

    // Create a temporary link to trigger download directly from GCS URL
    const link = document.createElement('a');
    link.href = pdfPath;
    link.target = '_blank';
    link.download = `${jdName || 'JD'}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this JD?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/job-description/${id}`);
        setJds(jds.filter((jd) => jd._id !== id));
      } catch (error) {
        alert('Failed to delete');
      }
    }
  };

  // --- Add JD via file upload ---
  const handleAddJdClick = () => {
    fileInputRef.current.click();
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const jdName = window.prompt('Please enter a name for this Job Description:');
    if (!jdName || jdName.trim() === '') {
      alert('Upload cancelled. A name is required.');
      event.target.value = null;
      return;
    }

    const formData = new FormData();
    formData.append('jdFile', file);
    formData.append('jd_name', jdName.trim());

    if (customerId) {
      formData.append('creatorId', customerId);
      formData.append('creatorRole', 'Customer');
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/job-description/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const newJd = response.data.data || response.data;
      setJds((prev) => [newJd, ...prev]);
      alert('JD uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload JD:', error);
      alert('Could not upload the JD.');
    } finally {
      event.target.value = null;
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'date' ? 'desc' : 'asc');
    }
  };

  /* ── Processed List ──────────────────────────────────────── */
  const processed = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();

    let list = jds.filter((jd) => {
      if (!q) return true;
      return (
        (jd.jd_name || '').toLowerCase().includes(q) ||
        (jd.job_role || '').toLowerCase().includes(q)
      );
    });

    if (sortField) {
      list = [...list].sort((a, b) => {
        if (sortField === 'date') {
          const aV = new Date(a.createdAt).getTime() || 0;
          const bV = new Date(b.createdAt).getTime() || 0;
          return sortDir === 'asc' ? aV - bV : bV - aV;
        }
        if (sortField === 'name') {
          const aV = (a.jd_name || a.job_role || '').toLowerCase();
          const bV = (b.jd_name || b.job_role || '').toLowerCase();
          return sortDir === 'asc' ? aV.localeCompare(bV) : bV.localeCompare(aV);
        }
        return 0;
      });
    }

    return list;
  }, [jds, searchQuery, sortField, sortDir]);

  /* ── Pagination ──────────────────────────────────────────── */
  const totalPages = Math.ceil(processed.length / ITEMS_PER_PAGE);
  const paginated = processed.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const getPageNums = () => {
    const pages = [];
    const max = 5;
    let start = Math.max(1, currentPage - Math.floor(max / 2));
    let end = Math.min(totalPages, start + max - 1);
    if (end - start < max - 1) start = Math.max(1, end - max + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const SortIcon = ({ field }) => {
    const active = sortField === field;
    return (
      <span className="cgj-sort-icons">
        <ChevronUp size={11} className={active && sortDir === 'asc' ? 'cgj-si-on' : ''} />
        <ChevronDown size={11} className={active && sortDir === 'desc' ? 'cgj-si-on' : ''} />
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'Invalid Date';
    }
  };

  /* ── Loading ─────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="cgj-root">
        <div className="cgj-loading">
          <div className="cgj-loading-card">
            <Loader2 className="cgj-spin" size={36} />
            <span>Loading your JDs…</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cgj-root">
      {/* Background blobs */}
      <div className="cgj-bg-blob cgj-blob-1" />
      <div className="cgj-bg-blob cgj-blob-2" />

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        accept=".pdf"
      />

      {/* ── Header ── */}
      <div className="cgj-header">
        <div className="cgj-header-left">
          <div className="cgj-title-icon">
            <FileText size={26} />
          </div>
          <div>
            <h1 className="cgj-title">My Generated JDs</h1>
            <p className="cgj-subtitle">
              Manage and edit all your job descriptions
            </p>
          </div>
        </div>

        <div className="cgj-header-right">
          <span className="cgj-count-badge">
            <FileText size={14} />
            {processed.length} JD{processed.length !== 1 ? 's' : ''}
          </span>
          <button className="cgj-add-btn" onClick={handleAddJdClick}>
            <Plus size={16} />
            Add JD
          </button>
        </div>
      </div>

      {/* ── Controls ── */}
      <div className="cgj-controls">
        {/* Search */}
        <div className="cgj-search-wrap">
          <Search size={16} className="cgj-search-icon" />
          <input
            className="cgj-search"
            type="text"
            placeholder="Search by JD name or job role…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="cgj-clear-btn" onClick={() => setSearchQuery('')}>
              <X size={12} />
            </button>
          )}
        </div>

        {/* Sort */}
        <div className="cgj-sort-bar">
          <span className="cgj-sort-label">
            <SlidersHorizontal size={13} /> Sort
          </span>
          <button
            className={`cgj-sort-btn ${sortField === 'date' ? 'active' : ''}`}
            onClick={() => handleSort('date')}
          >
            Date <SortIcon field="date" />
          </button>
          <button
            className={`cgj-sort-btn ${sortField === 'name' ? 'active' : ''}`}
            onClick={() => handleSort('name')}
          >
            Name <SortIcon field="name" />
          </button>
        </div>
      </div>

      {/* Showing */}
      <p className="cgj-showing">
        {searchQuery
          ? `Showing ${paginated.length} of ${processed.length} results for "${searchQuery}"`
          : `Showing ${paginated.length} of ${processed.length} JDs`}
      </p>

      {/* ── Cards Grid ── */}
      {paginated.length === 0 ? (
        <div className="cgj-empty-card">
          <FileText size={44} className="cgj-empty-icon" />
          <h3>{searchQuery ? `No JDs match "${searchQuery}"` : "You haven't generated any JDs yet"}</h3>
          <p>
            {searchQuery
              ? 'Try a different search term'
              : 'Click "Add JD" to upload one or use the JD Generator to create your first description'}
          </p>
          {!searchQuery && (
            <button className="cgj-add-btn-large" onClick={handleAddJdClick}>
              <FileUp size={16} /> Upload Your First JD
            </button>
          )}
        </div>
      ) : (
        <div className="cgj-grid">
          {paginated.map((jd) => (
            <div key={jd._id} className="cgj-card">
              <div className="cgj-card-top">
                <div className="cgj-card-icon">
                  <FileText size={20} />
                </div>
                <div className="cgj-card-head">
                  <h3 className="cgj-card-name" title={jd.job_role || jd.jd_name}>
                    {jd.job_role || jd.jd_name}
                  </h3>
                  {jd.jd_name && jd.job_role && jd.jd_name !== jd.job_role && (
                    <span className="cgj-card-role">{jd.jd_name}</span>
                  )}
                </div>
              </div>

              <div className="cgj-card-meta">
                <Calendar size={12} /> Created {formatDate(jd.createdAt)}
              </div>

              <div className="cgj-card-actions">
                <button
                  className="cgj-action cgj-action-view"
                  onClick={() => window.open(jd.pdf_path, '_blank')}
                  disabled={!jd.pdf_path}
                  title="Open PDF"
                >
                  <Eye size={14} /> View
                </button>
                <button
                  className="cgj-action cgj-action-download"
                  onClick={() => handleDownload(jd.pdf_path, jd.jd_name)}
                  disabled={!jd.pdf_path}
                  title="Download PDF"
                >
                  <Download size={14} />
                </button>
                <button
                  className="cgj-action cgj-action-edit"
                  onClick={() => handleEdit(jd._id)}
                  title="Edit in Generator"
                >
                  <Pencil size={14} />
                </button>
                <button
                  className="cgj-action cgj-action-delete"
                  onClick={() => handleDelete(jd._id)}
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="cgj-pagination">
          <span className="cgj-page-info">
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </span>
          <div className="cgj-page-controls">
            <button
              className="cgj-page-btn nav"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={14} />
            </button>
            {getPageNums().map((p) => (
              <button
                key={p}
                className={`cgj-page-btn ${currentPage === p ? 'active' : ''}`}
                onClick={() => setCurrentPage(p)}
              >
                {p}
              </button>
            ))}
            <button
              className="cgj-page-btn nav"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerGeneratedJDs;